import {
  ChainType,
  groupByChain,
  makePartialChainTypeRecord,
} from '@polaris/core/models'
import { featureFlagsAtom } from '@polaris/core/stores'
import { useAtomValue } from 'jotai'
import { useMemo } from 'react'
import { pipe, sortBy } from 'remeda'
import { useBalances } from '@/src/queries/hooks/balances'
import { mpcWalletAddressesWithNamadaOverrideAtom } from '@/src/stores/mpc/atoms'

export function useMpcBalances() {
  const featureFlags = useAtomValue(featureFlagsAtom)
  const { data: mpcAddresses } = useAtomValue(
    mpcWalletAddressesWithNamadaOverrideAtom,
  )

  const { data: cosmosBalances, isLoading: isLoadingCosmosBalances } =
    useBalances(
      mpcAddresses?.cosmos,
      featureFlags.cosmosEcosystem && !!mpcAddresses?.cosmos,
    )
  const { data: evmBalances, isLoading: isLoadingEvmBalances } = useBalances(
    mpcAddresses?.evm,
    featureFlags.evmEcosystem && !!mpcAddresses?.evm,
  )
  const { data: solanaBalances, isLoading: isLoadingSolanaBalances } =
    useBalances(
      mpcAddresses?.solana,
      featureFlags.solanaEcosystem && !!mpcAddresses?.solana,
    )
  // Context: https://linear.app/osmosis/issue/POLFE-1121/remove-bitcoin-connector
  // It's OK to offer utxo balances (Bitcoin) to allow recovery
  const { data: utxoBalances, isLoading: isLoadingUtxoBalances } = useBalances(
    mpcAddresses?.utxo,
    !!mpcAddresses?.utxo,
  )

  const balances = useMemo(() => {
    return makePartialChainTypeRecord((chainType) => {
      switch (chainType) {
        case ChainType.Cosmos:
          return cosmosBalances
        case ChainType.Evm:
          return evmBalances
        case ChainType.Solana:
          return solanaBalances
        case ChainType.Bitcoin:
          return utxoBalances
        default:
          return undefined
      }
    })
  }, [cosmosBalances, evmBalances, solanaBalances, utxoBalances])

  const totalUsdValue = useMemo(
    () =>
      (cosmosBalances?.valueUsd ?? 0) +
      (evmBalances?.valueUsd ?? 0) +
      (solanaBalances?.valueUsd ?? 0) +
      (utxoBalances?.valueUsd ?? 0),
    [cosmosBalances, evmBalances, solanaBalances, utxoBalances],
  )

  const balancesByChain = useMemo(
    () =>
      pipe(
        groupByChain(
          ...Object.values(balances).flatMap(({ balances }) => balances),
        ),
        sortBy([(item) => item.totalUsdValue, 'desc']),
      ),
    [balances],
  )

  const isLoadingBalances =
    isLoadingCosmosBalances ||
    isLoadingEvmBalances ||
    isLoadingSolanaBalances ||
    isLoadingUtxoBalances

  return {
    balances,
    balancesByChain,
    totalUsdValue,
    isLoadingBalances,
  }
}
